import './App.css';
import {useEffect, useState} from "react";
import PropTypes from "prop-types";

// Token helpers
const getToken = () => localStorage.getItem('token');
const setToken = (t) => t ? localStorage.setItem('token', t) : localStorage.removeItem('token');

// Authenticated fetch wrapper — adds Authorization header, dispatches auth:logout on 401
async function apiFetch(url, options = {}) {
    const token = getToken();
    const headers = {...(options.headers || {})};
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const res = await fetch(url, {...options, headers});
    if (res.status === 401) {
        window.dispatchEvent(new Event('auth:logout'));
    }
    return res;
}

function App() {
    const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'dark');
    const [isAuthenticated, setIsAuthenticated] = useState(!!getToken());
    const toggleTheme = () => setTheme(t => {
        const next = t === 'dark' ? 'light' : t === 'light' ? 'minimal' : 'dark';
        localStorage.setItem('theme', next);
        return next;
    });

    useEffect(() => {
        const handleAuthLogout = () => {
            setToken(null);
            setIsAuthenticated(false);
        };
        window.addEventListener('auth:logout', handleAuthLogout);
        return () => window.removeEventListener('auth:logout', handleAuthLogout);
    }, []);

    function handleLogin(token) {
        setToken(token);
        setIsAuthenticated(true);
    }

    async function handleLogout() {
        await apiFetch('/auth/logout', {method: 'POST'});
        setToken(null);
        setIsAuthenticated(false);
    }

    if (!isAuthenticated) {
        const wantsLogin = new URLSearchParams(window.location.search).has('signin');
        if (!wantsLogin) {
            window.location.replace('/landing.html');
            return null;
        }
        return (
            <div className="App" data-theme={theme}>
                <Header theme={theme} onToggleTheme={toggleTheme}/>
                <LoginScreen onLogin={handleLogin}/>
                <Footer/>
            </div>
        );
    }

    return (
        <div className="App" data-theme={theme}>
            <Header theme={theme} onToggleTheme={toggleTheme} onLogout={handleLogout}/>
            <ChecklistContainer/>
            <Footer/>
        </div>
    );
}

function Header({theme, onToggleTheme, onLogout}) {
    const nextIcon = theme === 'dark' ? <SunIcon/> : theme === 'light' ? <MinimalIcon/> : <MoonIcon/>;
    return (
        <div className="app-header">
            <button className="theme-toggle" onClick={onToggleTheme} aria-label="Toggle theme">
                {nextIcon}
            </button>
            {onLogout && (
                <button className="sign-out-button" onClick={onLogout}>Sign out</button>
            )}
        </div>
    )
}

Header.propTypes = {
    theme: PropTypes.string.isRequired,
    onToggleTheme: PropTypes.func.isRequired,
    onLogout: PropTypes.func,
};

function SunIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
            <line x1="8" y1="1" x2="8" y2="3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="8" y1="13" x2="8" y2="15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="1" y1="8" x2="3" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="13" y1="8" x2="15" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="2.93" y1="2.93" x2="4.34" y2="4.34" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="11.66" y1="11.66" x2="13.07" y2="13.07" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="2.93" y1="13.07" x2="4.34" y2="11.66" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="11.66" y1="4.34" x2="13.07" y2="2.93" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
    )
}

function MinimalIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 2.5 A5.5 5.5 0 0 1 8 13.5 Z" fill="currentColor"/>
        </svg>
    )
}

function MoonIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.5 10A6 6 0 1 1 6 2.5 4.5 4.5 0 0 0 13.5 10z"
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
    )
}

function LoginScreen({onLogin}) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');
        try {
            const res = await fetch('/auth/login', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({username, password}),
            });
            if (!res.ok) {
                setError('Invalid username or password');
                return;
            }
            const {token} = await res.json();
            onLogin(token);
        } catch {
            setError('Login failed. Please try again.');
        }
    }

    return (
        <div className="login-screen">
            <form className="login-form" onSubmit={handleSubmit}>
                <h2 className="login-title">Sign in</h2>
                {error && <p className="login-error">{error}</p>}
                <input
                    className="login-input"
                    type="text"
                    placeholder="Username"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    autoFocus
                />
                <input
                    className="login-input"
                    type="password"
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                />
                <button
                    className="login-button"
                    type="submit"
                    disabled={!username || !password}
                >Sign in</button>
            </form>
        </div>
    );
}

LoginScreen.propTypes = {
    onLogin: PropTypes.func.isRequired,
};

function Footer() {
    return (
        <div className="app-footer"></div>
    )
}

function ChecklistContainer() {
    const [checklistData, setChecklistData] = useState([])
    const [showModal, setShowModal] = useState(false)

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklists', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setChecklistData(data.sort((a, b) => a.id - b.id)))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, [])

    async function deleteChecklist(id) {
        await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
        setChecklistData(prev => prev.filter(c => c.id !== id));
    }

    async function putChecklist(checklistId, updated) {
        const res = await apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated)
        });
        const saved = await res.json();
        setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
    }

    function updateTitle(checklistId, title) {
        const checklist = checklistData.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }

    function updateItem(checklistId, itemId, title) {
        const checklist = checklistData.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, title} : i);
        putChecklist(checklistId, {...checklist, items});
    }

    function deleteItem(checklistId, itemId) {
        const checklist = checklistData.find(c => c.id === checklistId);
        const items = checklist.items.filter(i => i.id !== itemId);
        putChecklist(checklistId, {...checklist, items});
    }

    function addItem(checklistId, title) {
        const checklist = checklistData.find(c => c.id === checklistId);
        const newItem = {id: Math.max(0, ...checklist.items.map(i => i.id)) + 1, title, checked: false};
        putChecklist(checklistId, {...checklist, items: [newItem, ...checklist.items]});
    }

    function toggleItem(checklistId, itemId) {
        const checklist = checklistData.find(c => c.id === checklistId);
        const items = checklist.items.map(i => i.id === itemId ? {...i, checked: !i.checked} : i);
        putChecklist(checklistId, {...checklist, items});
    }

    async function addNewChecklist(title) {
        const res = await apiFetch('/checklists', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title, items: []})
        });
        const created = await res.json();
        setChecklistData(prev => [...prev, created]);
    }

    return (
        <div>
            <div>
                <button
                    className="add-checklist-button"
                    onClick={() => setShowModal(true)}
                >+ New List
                </button>
            </div>
            {showModal && (
                <NewChecklistModal
                    onConfirm={(title) => { addNewChecklist(title); setShowModal(false); }}
                    onCancel={() => setShowModal(false)}
                />
            )}
            <div className="checklists-container">
                {checklistData.map((checklist) => (
                    <Checklist
                        key={checklist.id}
                        data={checklist}
                        onDelete={deleteChecklist}
                        onAddItem={addItem}
                        onToggleItem={toggleItem}
                        onDeleteItem={deleteItem}
                        onUpdateTitle={updateTitle}
                        onUpdateItem={updateItem}
                    />
                ))}
            </div>
        </div>
    )
}

const itemShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    checked: PropTypes.bool.isRequired,
});

const checklistShape = PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(itemShape).isRequired,
});

function Checklist({data, onDelete, onAddItem, onToggleItem, onDeleteItem, onUpdateTitle, onUpdateItem}) {
    const [newItemTitle, setNewItemTitle] = useState('');
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');

    function handleAdd() {
        const title = newItemTitle.trim();
        if (!title) return;
        onAddItem(data.id, title);
        setNewItemTitle('');
    }

    function handleAddKeyDown(e) {
        if (e.key === 'Enter') handleAdd();
    }

    function startEditingTitle() {
        setTitleDraft(data.title);
        setEditingTitle(true);
    }

    function commitTitle() {
        const title = titleDraft.trim();
        if (title && title !== data.title) {
            onUpdateTitle(data.id, title);
        }
        setEditingTitle(false);
    }

    function handleTitleKeyDown(e) {
        if (e.key === 'Enter') commitTitle();
        if (e.key === 'Escape') setEditingTitle(false);
    }

    return (
        <div className="checklist">
            <div className="checklist-header">
                {editingTitle
                    ? <input
                        className="checklist-title-input"
                        value={titleDraft}
                        onChange={e => setTitleDraft(e.target.value)}
                        onBlur={commitTitle}
                        onKeyDown={handleTitleKeyDown}
                        autoFocus
                      />
                    : <h2
                        onClick={startEditingTitle}
                        onKeyDown={e => e.key === 'Enter' && startEditingTitle()}
                        tabIndex={0}
                      >{data.title}</h2>
                }
                <button onClick={() => onDelete(data.id)} aria-label="Delete checklist">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                </button>
            </div>
            <div className="checklist-add-item">
                <div className="checklist-add-item-inner">
                    <input
                        type="text"
                        placeholder="Add new item"
                        value={newItemTitle}
                        onChange={e => setNewItemTitle(e.target.value)}
                        onKeyDown={handleAddKeyDown}
                    />
                    <button onClick={handleAdd}>Add</button>
                </div>
            </div>
            {data.items.map((item) => (
                <ChecklistItem
                    key={item.id}
                    data={item}
                    onToggle={() => onToggleItem(data.id, item.id)}
                    onDelete={() => onDeleteItem(data.id, item.id)}
                    onUpdate={(title) => onUpdateItem(data.id, item.id, title)}
                />
            ))}
        </div>
    );
}

Checklist.propTypes = {
    data: checklistShape.isRequired,
    onDelete: PropTypes.func.isRequired,
    onAddItem: PropTypes.func.isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onDeleteItem: PropTypes.func.isRequired,
    onUpdateTitle: PropTypes.func.isRequired,
    onUpdateItem: PropTypes.func.isRequired,
};

function ChecklistItem({data, onToggle, onDelete, onUpdate}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');

    function startEditing() {
        setDraft(data.title);
        setEditing(true);
    }

    function commit() {
        const title = draft.trim();
        if (title && title !== data.title) onUpdate(title);
        setEditing(false);
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') setEditing(false);
    }

    return (
        <div className="checklist-item">
            <input
                type="checkbox"
                checked={data.checked}
                onChange={onToggle}
            />
            {editing
                ? <input
                    className="checklist-item-title-input"
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={handleKeyDown}
                    autoFocus
                  />
                : <span
                    className="checklist-item-label"
                    onClick={startEditing}
                    onKeyDown={e => e.key === 'Enter' && startEditing()}
                    role="button"
                    tabIndex={0}
                  >{data.title}</span>
            }
            <button className="checklist-item-delete" onClick={onDelete} aria-label="Delete item">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
            </button>
        </div>
    );
}

ChecklistItem.propTypes = {
    data: itemShape.isRequired,
    onToggle: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
};

function NewChecklistModal({onConfirm, onCancel}) {
    const [title, setTitle] = useState('');

    function handleKeyDown(e) {
        if (e.key === 'Enter') submit();
        if (e.key === 'Escape') onCancel();
    }

    function submit() {
        const t = title.trim();
        if (!t) return;
        onConfirm(t);
    }

    return (
        <div
            className="modal-backdrop"
            onClick={onCancel}
            onKeyDown={e => e.key === 'Escape' && onCancel()}
            role="presentation"
        >
            <div
                className="modal"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
            >
                <h3 className="modal-title">New List</h3>
                <input
                    className="modal-input"
                    type="text"
                    placeholder="List title"
                    value={title}
                    onChange={e => setTitle(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoFocus
                />
                <div className="modal-actions">
                    <button className="modal-cancel" onClick={onCancel}>Cancel</button>
                    <button className="modal-confirm" onClick={submit} disabled={!title.trim()}>Create</button>
                </div>
            </div>
        </div>
    );
}

NewChecklistModal.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default App;
